// Environments — static (long-lived) and dynamic (TTL) environments unified
// in one place, replacing the separate Dynamic Environments tab.

import React, { useEffect, useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Public as EnvironmentsIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { isInProgressStatus } from '../lib/grouping';
import { sortEnvNames } from '../lib/grouping';
import { getColors, rounded } from '../theme/tokens';
import { getEnvironmentHue } from '../theme/statusTokens';
import { isDestroyOperation } from '../../utils/serviceParser';
import { DeploymentRecord, EnvironmentInfo } from '../../types/DeploymentRecord';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import InfoPopover from '../components/InfoPopover';
import { timeAgo } from '../lib/format';

interface EnvSummary {
  env: string;
  dynamic: boolean;
  records: DeploymentRecord[];
  services: number;
  deploying: number;
  failed: number;
  destroyed: number;
  lastTimestamp: string | null;
  info?: EnvironmentInfo;
}

const summarize = (
  env: string,
  records: DeploymentRecord[],
  dynamic: boolean,
  info?: EnvironmentInfo
): EnvSummary => {
  const services = new Set(records.map(r => r.serviceName || r.service)).size;
  const deploying = records.filter(r => isInProgressStatus(r.status)).length;
  const failed = records.filter(r => r.status?.toLowerCase() === 'failed').length;
  const destroyed = records.filter(
    r => r.status?.toLowerCase() === 'success' && isDestroyOperation(r)
  ).length;
  const lastTimestamp = records.reduce<string | null>((max, r) => {
    if (!r.timestamp) return max;
    return !max || new Date(r.timestamp) > new Date(max) ? r.timestamp : max;
  }, null);
  return { env, dynamic, records, services, deploying, failed, destroyed, lastTimestamp, info };
};

const EnvCard: React.FC<{ summary: EnvSummary }> = ({ summary }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const hue = getEnvironmentHue(summary.env);

  const stats: Array<{ label: string; color: string }> = [
    ...(summary.deploying > 0 ? [{ label: `${summary.deploying} deploying`, color: '#0b76d1' }] : []),
    ...(summary.failed > 0 ? [{ label: `${summary.failed} failed`, color: '#d93a3a' }] : []),
    ...(summary.destroyed > 0 ? [{ label: `${summary.destroyed} destroyed`, color: '#787774' }] : []),
  ];

  return (
    <Paper
      component={RouterLink}
      to={`/environments/${encodeURIComponent(summary.env)}`}
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: `${rounded.lg}px`,
        borderColor: c.hairline,
        textDecoration: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minWidth: 0,
        '&:hover': { borderColor: c.inkFaint },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: hue, flexShrink: 0 }} />
        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: c.ink }} noWrap title={summary.env}>
          {summary.env}
        </Typography>
        {summary.dynamic && (
          <Typography variant="caption" sx={{ color: c.inkFaint }}>
            ⏳ TTL
          </Typography>
        )}
      </Box>
      <Typography variant="caption" sx={{ color: c.inkMuted }}>
        {summary.services} service{summary.services !== 1 ? 's' : ''}
        {summary.info?.account_name ? ` · ${summary.info.account_name}` : ''}
      </Typography>
      {stats.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {stats.map(s => (
            <Typography key={s.label} variant="caption" sx={{ color: s.color, fontWeight: 600 }}>
              {s.label}
            </Typography>
          ))}
        </Box>
      )}
      {summary.lastTimestamp && (
        <Typography variant="caption" sx={{ color: c.inkFaint }}>
          Last activity {timeAgo(summary.lastTimestamp)}
        </Typography>
      )}
    </Paper>
  );
};

const EnvironmentsPage: React.FC = () => {
  const {
    currentRecords,
    dynamicRecords,
    staleCurrentIds,
    environmentInfo,
    fetchEnvironmentInfo,
    wsStatus,
  } = useDeploymentData();

  // Drop stale/superseded failed & stuck deploys from the dynamic-env set too
  // (the static set already excludes them via currentRecords).
  const visibleDynamicRecords = useMemo(
    () => dynamicRecords.filter(r => !r.id || !staleCurrentIds.has(r.id)),
    [dynamicRecords, staleCurrentIds]
  );

  useEffect(() => {
    if (wsStatus === 'connected') fetchEnvironmentInfo();
  }, [wsStatus, fetchEnvironmentInfo]);

  const infoByEnv = useMemo(() => {
    const map = new Map<string, EnvironmentInfo>();
    environmentInfo.forEach(info => map.set(info.environment_name, info));
    return map;
  }, [environmentInfo]);

  const staticSummaries = useMemo(() => {
    const byEnv = new Map<string, DeploymentRecord[]>();
    currentRecords.forEach(r => {
      const env = r.envName || r.env || 'unknown';
      const list = byEnv.get(env);
      if (list) list.push(r);
      else byEnv.set(env, [r]);
    });
    return sortEnvNames(Array.from(byEnv.keys())).map(env =>
      summarize(env, byEnv.get(env)!, false, infoByEnv.get(env))
    );
  }, [currentRecords, infoByEnv]);

  const dynamicSummaries = useMemo(() => {
    const byEnv = new Map<string, DeploymentRecord[]>();
    visibleDynamicRecords.forEach(r => {
      const env = r.envName || r.env || 'unknown';
      const list = byEnv.get(env);
      if (list) list.push(r);
      else byEnv.set(env, [r]);
    });
    return Array.from(byEnv.keys())
      .sort()
      .map(env => summarize(env, byEnv.get(env)!, true, infoByEnv.get(env)));
  }, [visibleDynamicRecords, infoByEnv]);

  const grid = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
    gap: 1.5,
  } as const;

  return (
    <Box>
      <PageHeader
        title="Environments"
        subtitle="Long-lived environments plus short-lived dynamic environments"
      />

      <Typography variant="overline" sx={{ display: 'block', mb: 1, opacity: 0.7 }}>
        Environments
      </Typography>
      {staticSummaries.length === 0 ? (
        <EmptyState icon={<EnvironmentsIcon />} title="No environments reporting yet" />
      ) : (
        <Box sx={grid}>
          {staticSummaries.map(s => (
            <EnvCard key={s.env} summary={s} />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 4, mb: 1 }}>
        <Typography variant="overline" sx={{ opacity: 0.7 }}>
          Dynamic environments
        </Typography>
        <InfoPopover entry="dynamicEnvironment" size={14} />
      </Box>
      {dynamicSummaries.length === 0 ? (
        <Typography variant="body2" sx={{ opacity: 0.55 }}>
          No dynamic environments are active right now.
        </Typography>
      ) : (
        <Box sx={grid}>
          {dynamicSummaries.map(s => (
            <EnvCard key={s.env} summary={s} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default EnvironmentsPage;
